/**
 * Layout B step state (BLU-1376, handover 4.2).
 *
 * One derivation turns the wizard's raw step facts into the visual state every
 * shell primitive renders (rail badge, pane badge, pill, card border). Each
 * state differs by FORM, never by colour alone: the badge content (number,
 * tick, number on a dashed ring, lock, clock), the pill word and icon, and the
 * card border style.
 *
 * Reduced motion: callers that run a timer import `prefersReducedMotion` from
 * `@/lib/scroll-container`. It is deliberately not redefined here.
 */
import type { StepId } from './steps';

export type StepVisualState = 'current' | 'open' | 'done' | 'skipped' | 'waiting' | 'locked';

export interface StepStateInput {
  /** The step counts as complete (server signal, manual flag or skip). */
  complete: boolean;
  /** The step is complete because the operator skipped it. */
  skipped?: boolean;
  /** Submitted, waiting on someone else (a pending 3PL claim). Counts as complete for unlocking. */
  waiting?: boolean;
  /** Every visible step before it is complete. */
  unlocked: boolean;
  /** The first unlocked step that is not complete yet. */
  isCurrent?: boolean;
}

/**
 * Precedence: locked, then waiting, then skipped, then done, then current,
 * then open. A step that is not unlocked reads locked whatever else is true,
 * so a later step can never show Done ahead of an unfinished earlier one.
 */
export function deriveVisualState({ complete, skipped, waiting, unlocked, isCurrent }: StepStateInput): StepVisualState {
  if (!unlocked) return 'locked';
  if (waiting) return 'waiting';
  if (complete && skipped) return 'skipped';
  if (complete) return 'done';
  if (isCurrent) return 'current';
  return 'open';
}

/** States that count as finished for the progress count and the saved box. */
export function isSettledState(state: StepVisualState): boolean {
  return state === 'done' || state === 'skipped' || state === 'waiting';
}

export type StepGroup = 'connect' | 'products' | 'garments' | 'printing' | 'tryIt' | 'team';

/** Rail and eyebrow group per step (handover 4.4 Groups). */
export const STEP_GROUP: Record<StepId, StepGroup> = {
  shopify: 'connect',
  claim: 'connect',
  syncTag: 'products',
  typeKinds: 'products',
  metafields: 'products',
  codeTables: 'products',
  sync: 'products',
  customFiles: 'products',
  blankProducts: 'garments',
  blankInventory: 'garments',
  colourAliases: 'printing',
  ripRouting: 'printing',
  testOrder: 'tryIt',
  inviteTeam: 'team',
};

/** How long the Save button holds "Saved" before the pane moves on (DESIGN 5). */
export const SETUP_SAVED_HOLD_MS = 700;
